<template>
  <div
    v-if="showModal"
    :id="id"
    :ref="id"
    class="modal-open show"
    tabindex="0"
    aria-hidden="true"
    @keydown.esc="checkKeyEvent"
    @keydown.tab="checkKeyEvent"
    @click.prevent="clickBackdrop"
  >
    <div :ref="`${id}-opacity`" class="modal-backdrop show" :style="computedBackdropZindex" />
    <div :ref="`${id}-container`" class="modal show" style="display: block;">
      <div
        :ref="`${id}-dialog`"
        :class="dialogClass"
        aria-modal="true"
        aria-labelledby="modal"
        role="dialog"
        tabindex="0"
        :style="computedDialogZindex"
      >
        <div :class="`modal-content border-0 rounded-0 modal-${color}`">
          <div class="modal-header">
            <!-- @slot Permet d'insérer n'importe quel composant vue dans le header du Modal à gauche du bouton close (croix) si closeButton = true 
            -->
            <slot name="header" />
            <button
              :ref="`${id}-close-button`"
              v-if="closeButton"
              type="button"
              class="btn-close"
              aria-label="Close"
              data-bs-dismiss="modal"
              @click.prevent="close"
            />
          </div>
          <div class="modal-body" v-if="hasBody">
            <!-- @slot Permet d'insérer n'importe quel composant vue dans le body du Modal. -->
            <slot name="body" />
          </div>
          <div class="modal-footer" v-if="hasFooter">
            <!-- @slot Permet d'insérer n'importe quel composant vue dans le footer du Modal. -->
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import generateUid from "@/clir/utils/generateUid/";

const ESCAPE = 27;
const TAB = 9;

/**
 * Composant Modal
 * 
 * @author <span><span class="fw-bold">Nicolas Huber</span><a class="ms-2" href="mailto:dmod-huber.consultant@dgfip.finances.gouv.fr">dmod-huber.consultant@dgfip.finances.gouv.fr</a></span>
 * @since <span class="fw-bold">Version 1.0</span>
 * @displayName Modal
 */
export default {
  name: "Modal",
  data() {
    return {
      focusIndex: -1
    };
  },
  props: {
    /**
     * Id de la Modal, autogénéré si non spécifié.
     */
    id: {
      type: String,
      default: () => `modal-${generateUid()}`
    },
    /**
     * Permet de faire apparaître/disparaître la Modal.
     */
    showModal: { type: Boolean, default: true },
    /**
     * Détermine la présence du bouton croix pour déclencher l'event <span class="text-partenaire">close</span>.
     */
    closeButton: { type: Boolean, default: true },
    /**
     * Détermine si un click sur le backdrop peut déclencher l'event <span class="text-partenaire">close</span>.
     */
    closable: { type: Boolean, default: true },
    /**
     * Détermine si la touche <span class="badge bg-dark">ECHAP</span> peut envoyer l'event <span class="text-partenaire">close</span>.
     */
    echappable: { type: Boolean, default: true },
    /**
     * Détermine la couleur de la fenêtre Modal.
     */
    color: { type: String, default: "" },
    /**
     * Le z-index de la fenêtre Modal.
     */
    zindex: { type: Number, default: 0 },
    /**
     * Centre la fenêtre Modal verticalement.
     */
    isVerticallyCentered: { type: Boolean, default: false },
    /**
     * Taille de la fenêtre Modal.
     * 
     * @values sm, "", lg, xl
     */
    size: {
      type: String,
      default: "",
      validator: value => ["", "sm", "lg", "xl", "auto"].indexOf(value) !== -1
    }
  },
  computed: {
    hasBody() {
      return !!this.$slots["body"];
    },
    hasFooter() {
      return !!this.$slots["footer"];
    },
    computedBackdropZindex() {
      return this.zindex ? `z-index:${this.zindex};` : "";
    },
    computedDialogZindex() {
      return this.zindex ? "" : `z-index:${this.zindex + 1};`;
    },
    dialogClass() {
      return `modal-dialog ${
        this.isVerticallyCentered ? "modal-dialog-centered" : ""
      } ${this.modalSize}`;
    },
    modalSize() {
      return (this.size.length ? "modal-" : "") + this.size;
    }
  },
  watch: {
    showModal(newVal) {
      if (newVal) {
        this.$nextTick(function() {
          if (this.closeButton) {
            this.$refs[`${this.id}-close-button`].focus();
            this.focusIndex = this.closeButton ? 0 : -1;
          } else {
            this.$refs[`${this.id}-dialog`].focus();
          }
        });
      } else {
        this.focusIndex = -1;
      }
    }
  },
  methods: {
    clickBackdrop(event) {
      const isEvent =
        event && event.target === this.$refs[`${this.id}-container`];
      if (this.closable && isEvent) this.close();
    },
    checkKeyEvent(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.echappable && event.keyCode === ESCAPE) return this.close();
      const focusableList = this.$refs[`${this.id}-dialog`].querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      let index = this.focusIndex;
      if (event.keyCode === TAB) {
        index += event.shiftKey === true ? -1 : 1;
        if (index < focusableList.length && index >= 0) {
          focusableList[index].focus();
        } else {
          this.$refs[`${this.id}-dialog`].focus();
          index = -1;
        }
        this.focusIndex = index;
        return;
      }
    },
    close() {
      /**
      * Cet évènement est déclenché par :
      *
      * * touche <span class="badge bg-dark">ECHAP</span> si <span class="text-partenaire">echappable</span> = true
      * 
      * * click sur la croix si <span class="text-partenaire">closeButton</span> = true
      * 
      * @property {Object} event l'évènement javascript natif.
      */
      this.$emit("close");
    }
  }
};
</script>

<style scoped>
.modal-header {
  font-weight: 700;
  border-bottom: 0;
  padding-bottom: 8px;
  text-align: left;
  width: 100%;
}
.modal-footer {
  border-top: none;
}

.defaultCursor {
  cursor: default;
}
</style>

<docs>
```[import](./__example__/ModalExample.vue)
```
</docs>